import { getToolName, isToolUIPart, type UIMessage } from "ai";
import type { RagCitation } from "@/apps/rag/types.js";

export type KnowledgeSearchStatus = "idle" | "searching" | "preparing";

const KNOWLEDGE_SEARCH_TOOL = "knowledge-search";

function isToolComplete(state: string | undefined): boolean {
  return state === "output-available" || state === "output-error";
}

function isToolInProgress(state: string | undefined, streaming: boolean): boolean {
  if (!streaming) return false;
  if (!state) return true;
  return !isToolComplete(state);
}

function queryFromToolInput(input: unknown): string | null {
  if (!input || typeof input !== "object") return null;
  const query = (input as { query?: unknown }).query;
  if (typeof query !== "string") return null;
  const trimmed = query.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Strip untrusted-data wrappers from tool excerpt text for display. */
export function unwrapKnowledgeExcerpt(text: string): string {
  const trimmed = text.trim();
  const fence = trimmed.match(/```(?:text)?\s*([\s\S]*?)```/);
  if (fence?.[1]) return fence[1].trim();
  return trimmed.replace(/^<!--[\s\S]*?-->\s*/, "").trim();
}

type ToolSource = {
  id?: unknown;
  documentId?: unknown;
  title?: unknown;
  pageNumber?: unknown;
  documentVersionId?: unknown;
  versionDownloadPath?: unknown;
  versionLabel?: unknown;
  libraryId?: unknown;
  attachmentId?: unknown;
  score?: unknown;
  text?: unknown;
};

function asOptionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

function asOptionalNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function citationFromToolSource(entry: ToolSource): RagCitation | null {
  if (typeof entry.id !== "number" || !Number.isFinite(entry.id)) return null;
  const excerptRaw = typeof entry.text === "string" ? entry.text : "";
  const excerpt = unwrapKnowledgeExcerpt(excerptRaw);
  const title =
    asOptionalString(entry.title) ??
    asOptionalString(entry.documentId) ??
    `Source ${entry.id}`;
  const score = asOptionalNumber(entry.score) ?? 0;
  const pageNumber = asOptionalNumber(entry.pageNumber);

  return {
    id: String(entry.id),
    index: entry.id,
    documentId: asOptionalString(entry.documentId),
    attachmentId: asOptionalString(entry.attachmentId),
    documentVersionId: asOptionalString(entry.documentVersionId),
    versionDownloadPath: asOptionalString(entry.versionDownloadPath),
    versionLabel: asOptionalString(entry.versionLabel),
    documentTitle: title,
    page: pageNumber != null ? String(pageNumber) : undefined,
    relevance: score <= 1 ? Math.round(score * 100) : Math.round(score),
    excerpt,
  };
}

function citationsFromToolOutput(output: unknown): RagCitation[] {
  if (!output || typeof output !== "object") return [];
  const record = output as { refused?: unknown; sources?: unknown };
  if (record.refused === true) return [];
  if (!Array.isArray(record.sources)) return [];

  const citations: RagCitation[] = [];
  const seen = new Set<string>();

  for (const entry of record.sources) {
    if (!entry || typeof entry !== "object") continue;
    const citation = citationFromToolSource(entry as ToolSource);
    if (!citation) continue;
    const dedupeKey = [
      citation.documentId ?? "",
      citation.page ?? "",
      citation.excerpt.slice(0, 80),
    ].join("|");
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    citations.push(citation);
  }

  return citations;
}

/**
 * Parses knowledge-search tool parts on an assistant message into status + citations.
 */
export function inspectKnowledgeSearchMessage(
  message: UIMessage | null | undefined,
  options?: { hasText?: boolean; streaming?: boolean; isKnowledgeRoute?: boolean },
): {
  status: KnowledgeSearchStatus;
  statusLabel: string | null;
  query: string | null;
  citations: RagCitation[];
  refused: boolean;
  refusedMessage: string | null;
  hasKnowledgeTools: boolean;
} {
  const hasText = options?.hasText ?? false;
  const streaming = options?.streaming ?? false;
  const isKnowledgeRoute = options?.isKnowledgeRoute ?? false;

  const parts = Array.isArray(message?.parts) ? message!.parts : [];
  let hasKnowledgeTools = false;
  let searchInProgress = false;
  let query: string | null = null;
  let refused = false;
  let refusedMessage: string | null = null;
  const citations: RagCitation[] = [];
  const seenIds = new Set<string>();

  for (const part of parts) {
    if (!isToolUIPart(part)) continue;
    const name = getToolName(part);
    if (name !== KNOWLEDGE_SEARCH_TOOL) continue;

    hasKnowledgeTools = true;
    if (isToolInProgress(part.state, streaming)) searchInProgress = true;
    const partQuery = queryFromToolInput(part.input);
    if (partQuery) query = partQuery;

    if (part.state === "output-available" || part.output != null) {
      const output = part.output;
      if (output && typeof output === "object") {
        const record = output as { refused?: unknown; message?: unknown };
        if (record.refused === true) {
          refused = true;
          refusedMessage =
            typeof record.message === "string" && record.message.trim()
              ? record.message.trim()
              : "No relevant evidence was found in the knowledge base.";
        }
      }
      for (const citation of citationsFromToolOutput(output)) {
        if (seenIds.has(citation.id)) continue;
        seenIds.add(citation.id);
        citations.push(citation);
      }
    }
  }

  let status: KnowledgeSearchStatus = "idle";
  if (searchInProgress) {
    status = "searching";
  } else if (hasKnowledgeTools && !hasText && streaming) {
    status = "preparing";
  } else if (isKnowledgeRoute && !hasKnowledgeTools && !hasText && streaming) {
    status = "searching";
  }

  const statusLabel =
    status === "searching"
      ? "Searching your knowledge base…"
      : status === "preparing"
        ? "Preparing your answer…"
        : null;

  return {
    status,
    statusLabel,
    query,
    citations,
    refused,
    refusedMessage,
    hasKnowledgeTools,
  };
}
